/* eslint-disable @typescript-eslint/no-misused-promises */
/**
 * Authentication Context and Provider handle all authentication states
 *
 * @remarks
 * Handles authentication states only.
 * API call with cleanup function included only for handling persisted authentication
 * For other Authentication API calls, @see @link {src/_actions/AuthActions.ts}
 */

import { useQueryClient } from "@tanstack/react-query";
import { useLogoutUserQuery } from "_actions/AuthActions";
import {
  defaultJobPayload,
  defaultSavedJobPayload,
  jobPayloadState,
  savedJobPayloadState,
  savedJobsSortByState,
  selectedSortByState,
  surveyCompletedState,
} from "_state/atoms/AppAtoms";
import { localstorageKeys } from "config";
import { SurveyRoutes } from "config/Consts";
import { useGetSurveysInfoQuery, useGetUserProfileQuery } from "queries";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { useLocalStorage } from "../hooks/useLocalstorage";

type TAuthContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => JSX.Element;

interface IAuthState {
  email?: string;
}

export interface IAuthContext {
  isAuthenticated: boolean;
  token: string;
  userDetails: any;
  authState: IAuthState | undefined;
  /**
   * @typeParam T() should be memoized with useCallback hook
   */
  setAuthState: (data: IAuthState) => void;
  setAuthToken: (accessToken: string) => void;
  logout: () => void;
  refetchProfile: () => void;
  isUnlockedServices: boolean;
}

/* PRE-AUTHENTICATION CONTEXT VALUES */
const AuthContextDefault: IAuthContext = {
  userDetails: null,
  isAuthenticated: false,
  token: "",
  authState: {},
  setAuthState: (data: IAuthState) => undefined,
  setAuthToken: (accessToken: string) => undefined,
  logout: () => undefined,
  refetchProfile: () => undefined,
  isUnlockedServices: false,
};
//
/* PRIMARY CONTEXT DECLARATION */
const AuthContext = React.createContext<IAuthContext>(AuthContextDefault);

/* AUTHENTICATION CONTEXT PROVIDER */
export const AuthContextProvider: TAuthContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const history = useHistory();
  const location = useLocation();

  const {
    data: userData,
    refetch: refetchProfile,
    dataUpdatedAt,
  } = useGetUserProfileQuery();

  // get suveys info for enabling and disabling other options
  const { data: surveysInfoRaw } = useGetSurveysInfoQuery();
  // Normalize: handle both raw array and axios-wrapped response { data: [] }
  const surveysInfo: any[] | undefined = Array.isArray(surveysInfoRaw)
    ? surveysInfoRaw
    : Array.isArray(surveysInfoRaw?.data)
    ? surveysInfoRaw.data
    : undefined;
  const { mutate: logoutUser } = useLogoutUserQuery();

  const queryClient = useQueryClient();
  const setSurveyCompleted = useSetRecoilState(surveyCompletedState);

  const [token, setAuthToken] = useLocalStorage(localstorageKeys.AUTH_TOKEN);
  const [isAuthenticated, setIsAuthenticated] = useState(
    Boolean(token?.accessToken)
  );

  const [authState, setAuthState] = useState<IAuthState>();
  const [userDetails, setUserProfileDetails] = useLocalStorage(
    localstorageKeys.USER_PROFILE
  );

  const setJobPayloadState = useSetRecoilState(jobPayloadState);
  const setSortByState = useSetRecoilState(selectedSortByState);
  const setSavedJobPayloadState = useSetRecoilState(savedJobPayloadState);
  const setSavedJobSortByState = useSetRecoilState(savedJobsSortByState);
  // when authentication status changed reset survey status
  useEffect(() => {
    setSurveyCompleted(false);
  }, [isAuthenticated]);

  // user is eligible but try to access eligibility survey/ user is not verified
  useEffect(() => {
    if (
      (userData?.is_email_verified === false ||
        (userData?.isEligibilitySurveyDone === true &&
          userData?.isEligible === false)) &&
      location?.pathname !== "/about"
    )
      history.replace("/");
  }, [location?.pathname]);

  // redirect to eligibility survey if not done
  useEffect(() => {
    if (
      (userData as boolean) &&
      userData?.is_email_verified === true &&
      userData?.isEligibilitySurveyDone === false
    ) {
      history.replace(SurveyRoutes.elgiblity);
    }
  }, [
    userData,
    userData?.is_email_verified,
    userData?.isEligibilitySurveyDone,
    history,
  ]);

  useEffect(() => {
    if (Boolean(token?.accessToken) && token?.accessToken !== "") {
      refetchProfile();
      setIsAuthenticated(true);
    }
  }, [refetchProfile, token?.accessToken]);

  useEffect(() => {
    if (
      Boolean(dataUpdatedAt) &&
      dataUpdatedAt > 0 &&
      Boolean(token?.accessToken) &&
      token?.accessToken !== ""
    ) {
      setUserProfileDetails(userData);
      setIsAuthenticated(true);
    }
  }, [dataUpdatedAt, userData]);

  // redirect to homepage if user logged in but not verified
  useEffect(() => {
    if (
      (userData as boolean) &&
      !(isAuthenticated && (userData?.is_email_verified as boolean))
    ) {
      history.replace("/");
    }
  }, [isAuthenticated, userDetails]);

  /**
   * Resets all auth states
   * @function setLoggedOut
   */

  const logout = useCallback(() => {
    // logout API & clear storage

    logoutUser(undefined, {
      onSettled: () => {
        setJobPayloadState(defaultJobPayload);
        setSavedJobPayloadState(defaultSavedJobPayload);
        setSavedJobSortByState([]);
        setSortByState([]);
      },
    });

    setTimeout(() => {
      localStorage.clear();
      setUserProfileDetails(null);
      setAuthToken();
      setIsAuthenticated(false);

      try {
        const invalidateAllCache: () => void = async () => {
          await queryClient.invalidateQueries();
        };
        invalidateAllCache();
        queryClient.clear();
      } catch (error) {}
    }, 0);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        userDetails,
        isAuthenticated,
        logout,
        setAuthToken,
        token,
        authState,
        setAuthState,
        refetchProfile,
        isUnlockedServices: surveysInfo?.[0]?.isCompleted ?? false,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
export default AuthContext;
